export const CURRENT_PROJECT_SCHEMA = 3;

export type BlendMode = 'normal' | 'multiply' | 'screen' | 'overlay' | 'darken' | 'lighten';

export interface ProjectAsset {
  id: string;
  name: string;
  mimeType: string;
  byteLength: number;
  width: number;
  height: number;
  storageKey: string;
  createdAt: string;
  role?: 'source' | 'working' | 'imported-source' | 'imported-working';
}

export interface NormalizedPoint {
  x: number;
  y: number;
}

export interface ElementBounds {
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface ElementBase {
  id: string;
  name: string;
  visible: boolean;
  locked: boolean;
  opacity: number;
  rotation: number;
  zIndex: number;
  bounds: ElementBounds;
}

export interface StrokeStyle {
  strokeColor: string;
  strokeWidth: number;
  fillColor: string | null;
  fillOpacity: number;
}

export interface ShapeElement extends ElementBase, StrokeStyle {
  type: 'rect' | 'ellipse' | 'polygon' | 'star';
  cornerRadius: number;
  sides: number;
}

export interface FreeformShapeElement extends ElementBase, StrokeStyle {
  type: 'freeform';
  points: NormalizedPoint[];
}

export interface PolylineShapeElement extends ElementBase, StrokeStyle {
  type: 'polyline';
  points: NormalizedPoint[];
}

export interface LineElement extends ElementBase {
  type: 'line' | 'arrow';
  points: [NormalizedPoint, NormalizedPoint];
  controlPoint?: NormalizedPoint;
  strokeColor: string;
  strokeWidth: number;
  arrowEnds: 'none' | 'start' | 'end' | 'both';
  arrowStyle: 'straight' | 'curved';
}

export interface DrawElement extends ElementBase {
  type: 'pencil' | 'marker' | 'brush' | 'pen';
  points: NormalizedPoint[];
  closed: boolean;
  strokeColor: string;
  strokeWidth: number;
}

export interface TextElement extends ElementBase {
  type: 'text';
  text: string;
  color: string;
  backgroundColor: string | null;
  outlineColor: string | null;
  fontFamily: string;
  fontSize: number;
  fontWeight: number;
  textAlign: 'left' | 'center' | 'right';
  curve: number;
}

export interface NumberElement extends ElementBase {
  type: 'number';
  number: number;
  sequenceMode?: 'fixed' | 'auto';
  color: string;
  backgroundColor: string | null;
  fontFamily: string;
  fontSize: number;
  fontWeight: number;
}

export interface ImageElement extends ElementBase {
  type: 'image';
  assetId: string;
  crop: ElementBounds;
}

export type MarkupElement = ShapeElement | FreeformShapeElement | PolylineShapeElement | LineElement | DrawElement | TextElement | NumberElement | ImageElement;
export type MarkupType = MarkupElement['type'];

export interface RasterLayer {
  id: string;
  type: 'raster';
  name: string;
  visible: boolean;
  locked: true;
  zIndex: 0;
  assetId: string;
}

export interface MarkupLayer {
  id: string;
  type: 'markup';
  name: string;
  visible: boolean;
  locked: boolean;
  zIndex: 1;
  elements: MarkupElement[];
}

export type ProjectLayer = RasterLayer | MarkupLayer;

export interface ExportDefaults {
  format: 'jpeg' | 'png' | 'webp';
  quality: number;
  preserveMetadata: boolean;
}

export interface RealImagerProject {
  schemaVersion: typeof CURRENT_PROJECT_SCHEMA;
  id: string;
  name: string;
  createdAt: string;
  updatedAt: string;
  canvas: {
    width: number;
    height: number;
    background: string;
  };
  assets: ProjectAsset[];
  layers: ProjectLayer[];
  exportDefaults: ExportDefaults;
  thumbnailDataUrl?: string;
}

export const DEFAULT_STROKE = '#ff3b30';
export const DEFAULT_FILL = '#ff3b30';

export function getPhotoLayer(project: RealImagerProject): RasterLayer {
  const layer = project.layers.find((item): item is RasterLayer => item.type === 'raster');
  if (!layer) throw new Error('Project photo is missing.');
  return layer;
}

export function getMarkupLayer(project: RealImagerProject): MarkupLayer {
  const layer = project.layers.find((item): item is MarkupLayer => item.type === 'markup');
  if (!layer) throw new Error('Project overlay is missing.');
  return layer;
}

export function replaceMarkupElements(project: RealImagerProject, elements: MarkupElement[]): RealImagerProject {
  return {
    ...project,
    layers: project.layers.map((layer) => layer.type === 'markup' ? { ...layer, elements } : layer)
  };
}

export function migrateProject(input: unknown): RealImagerProject {
  if (!input || typeof input !== 'object') throw new Error('This is not a valid RealImager project.');
  const raw = input as Record<string, unknown>;
  if (raw.schemaVersion === CURRENT_PROJECT_SCHEMA) return validateV3(raw);
  if (raw.schemaVersion === 2) return migrateV2(raw);
  if (raw.schemaVersion === 1) return migrateV1(raw);
  throw new Error(`Unsupported RealImager project version: ${String(raw.schemaVersion)}`);
}

function validateV3(raw: Record<string, unknown>): RealImagerProject {
  if (!raw.id || !raw.name || !raw.canvas || !Array.isArray(raw.layers) || !Array.isArray(raw.assets)) {
    throw new Error('The RealImager project is incomplete.');
  }
  const project = raw as unknown as RealImagerProject;
  const photo = project.layers.find((layer) => layer.type === 'raster');
  const overlay = project.layers.find((layer) => layer.type === 'markup');
  if (!photo || !overlay) {
    throw new Error('The RealImager project is missing its photo or overlay.');
  }
  if (!Number.isFinite(project.canvas.width) || project.canvas.width <= 0 || !Number.isFinite(project.canvas.height) || project.canvas.height <= 0) {
    throw new Error('The RealImager project has invalid image dimensions.');
  }
  if (!project.assets.some((asset) => asset.id === photo.assetId)) throw new Error('The RealImager project is missing its source asset.');
  if (!Array.isArray(overlay.elements) || overlay.elements.some((element) => !validElement(element))) {
    throw new Error('The RealImager project contains an invalid annotation.');
  }
  if (overlay.elements.some((element) => element.type === 'image' && !project.assets.some((asset) => asset.id === element.assetId))) {
    throw new Error('The RealImager project is missing an inserted image asset.');
  }
  return project;
}

function validElement(element: MarkupElement): boolean {
  const allowed: MarkupType[] = ['rect', 'ellipse', 'polygon', 'star', 'freeform', 'polyline', 'line', 'arrow', 'pencil', 'marker', 'brush', 'pen', 'text', 'number', 'image'];
  if (!element || !allowed.includes(element.type) || !element.id || !element.bounds) return false;
  const bounds = element.bounds;
  if (![bounds.x, bounds.y, bounds.width, bounds.height].every(Number.isFinite) || bounds.width <= 0 || bounds.height <= 0) return false;
  if ((element.type === 'line' || element.type === 'arrow') && (!Array.isArray(element.points) || element.points.length !== 2)) return false;
  if ((element.type === 'pencil' || element.type === 'marker' || element.type === 'brush' || element.type === 'pen') && (!Array.isArray(element.points) || element.points.length < 2)) return false;
  if ((element.type === 'freeform' || element.type === 'polyline') && (!Array.isArray(element.points) || element.points.length < 3)) return false;
  if (element.type === 'image' && typeof element.assetId !== 'string') return false;
  if (element.type === 'image' && !validCrop(element.crop)) return false;
  if (element.type === 'text' && typeof element.text !== 'string') return false;
  if (element.type === 'number' && !Number.isFinite(element.number)) return false;
  if (element.type === 'number' && element.sequenceMode !== undefined && element.sequenceMode !== 'fixed' && element.sequenceMode !== 'auto') return false;
  return true;
}

function validCrop(crop: ElementBounds): boolean {
  if (!crop || ![crop.x, crop.y, crop.width, crop.height].every(Number.isFinite)) return false;
  if (crop.x < 0 || crop.y < 0 || crop.width <= 0 || crop.height <= 0) return false;
  return crop.x + crop.width <= 1.000001 && crop.y + crop.height <= 1.000001;
}

function migrateV2(raw: Record<string, unknown>): RealImagerProject {
  if (!Array.isArray(raw.layers)) return validateV3({ ...raw, schemaVersion: CURRENT_PROJECT_SCHEMA });
  const layers = (raw.layers as Array<Record<string, unknown>>).map((layer) => {
    if (layer.type !== 'markup' || !Array.isArray(layer.elements)) return layer;
    return {
      ...layer,
      elements: (layer.elements as Array<Record<string, unknown>>).map((element) => element.type === 'image'
        ? { ...element, crop: { x: 0, y: 0, width: 1, height: 1 } }
        : element)
    };
  });
  return validateV3({ ...raw, schemaVersion: CURRENT_PROJECT_SCHEMA, layers });
}

function migrateV1(raw: Record<string, unknown>): RealImagerProject {
  if (!raw.id || !raw.name || !raw.canvas || !Array.isArray(raw.layers) || !Array.isArray(raw.assets)) {
    throw new Error('The RealImager project is incomplete.');
  }
  const legacyLayers = raw.layers as Array<Record<string, unknown>>;
  const legacyPhoto = legacyLayers.find((layer) => layer.type === 'raster');
  if (!legacyPhoto?.assetId) throw new Error('The RealImager project is missing its photo.');
  const legacyElements = legacyLayers
    .filter((layer) => layer.type === 'markup')
    .flatMap((layer) => Array.isArray(layer.elements) ? layer.elements as Array<Record<string, unknown>> : []);
  const migratedMarkup = legacyElements.map(migrateLegacyElement).filter((element): element is MarkupElement => Boolean(element));
  const migratedImages = legacyLayers
    .filter((layer) => layer.type === 'raster' && layer !== legacyPhoto && typeof layer.assetId === 'string')
    .map(migrateLegacyRasterElement);
  const elements = [...migratedMarkup, ...migratedImages]
    .sort((first, second) => first.zIndex - second.zIndex)
    .map((element, zIndex) => ({ ...element, zIndex }));
  const canvas = raw.canvas as Record<string, unknown>;
  const now = new Date().toISOString();
  return {
    schemaVersion: CURRENT_PROJECT_SCHEMA,
    id: String(raw.id),
    name: String(raw.name),
    createdAt: typeof raw.createdAt === 'string' ? raw.createdAt : now,
    updatedAt: typeof raw.updatedAt === 'string' ? raw.updatedAt : now,
    canvas: {
      width: positiveNumber(canvas.width, 1),
      height: positiveNumber(canvas.height, 1),
      background: typeof canvas.background === 'string' ? canvas.background : '#ffffff'
    },
    assets: raw.assets as ProjectAsset[],
    layers: [
      {
        id: typeof legacyPhoto.id === 'string' ? legacyPhoto.id : 'photo',
        type: 'raster',
        name: 'Photo',
        visible: legacyPhoto.visible !== false,
        locked: true,
        zIndex: 0,
        assetId: String(legacyPhoto.assetId)
      },
      {
        id: 'overlay',
        type: 'markup',
        name: 'Objects',
        visible: true,
        locked: false,
        zIndex: 1,
        elements
      }
    ],
    exportDefaults: normalizeExportDefaults(raw.exportDefaults),
    thumbnailDataUrl: typeof raw.thumbnailDataUrl === 'string' ? raw.thumbnailDataUrl : undefined
  };
}

function migrateLegacyRasterElement(raw: Record<string, unknown>, index: number): ImageElement {
  const transform = raw.transform && typeof raw.transform === 'object'
    ? raw.transform as Record<string, unknown>
    : {};
  const width = Math.max(0.005, Math.abs(numberValue(transform.scaleX, 1)));
  const height = Math.max(0.005, Math.abs(numberValue(transform.scaleY, 1)));
  const translateX = numberValue(transform.x, 0);
  const translateY = numberValue(transform.y, 0);
  return {
    id: typeof raw.id === 'string' ? raw.id : `migrated-image-${index}`,
    type: 'image',
    name: typeof raw.name === 'string' ? raw.name : `Image ${index + 1}`,
    visible: raw.visible !== false,
    locked: raw.locked === true,
    opacity: clampNumber(raw.opacity, 0, 1, 1),
    rotation: numberValue(transform.rotation, 0),
    zIndex: Number.isFinite(raw.zIndex) ? Number(raw.zIndex) : index,
    bounds: {
      x: translateX + (1 - width) / 2,
      y: translateY + (1 - height) / 2,
      width,
      height
    },
    assetId: String(raw.assetId),
    crop: { x: 0, y: 0, width: 1, height: 1 }
  };
}

function migrateLegacyElement(raw: Record<string, unknown>, index: number): MarkupElement | null {
  const type = String(raw.type ?? '');
  const points = normalizePoints(raw.points);
  const bounds = boundsFromPoints(points);
  const style = (raw.style && typeof raw.style === 'object' ? raw.style : {}) as Record<string, unknown>;
  const base: ElementBase = {
    id: typeof raw.id === 'string' ? raw.id : `migrated-${index}`,
    name: legacyName(type, index),
    visible: raw.visible !== false,
    locked: raw.locked === true,
    opacity: clampNumber(style.opacity, 0, 1, 1),
    rotation: numberValue(raw.rotation, 0),
    zIndex: Number.isFinite(raw.zIndex) ? Number(raw.zIndex) : index,
    bounds
  };
  const strokeColor = typeof style.strokeColor === 'string' ? style.strokeColor : DEFAULT_STROKE;
  const strokeWidth = positiveNumber(style.strokeWidth, 5);
  if (type === 'rect' || type === 'ellipse' || type === 'polygon' || type === 'diamond') {
    return {
      ...base,
      type: type === 'diamond' ? 'polygon' : type,
      strokeColor,
      strokeWidth,
      fillColor: typeof style.fillColor === 'string' ? style.fillColor.slice(0, 7) : null,
      fillOpacity: typeof style.fillColor === 'string' && style.fillColor.length > 7 ? 0.14 : 1,
      cornerRadius: 0,
      sides: type === 'diamond' ? 4 : type === 'polygon' ? Math.max(3, points.length) : 4
    };
  }
  if (type === 'line' || type === 'arrow') {
    const pair: [NormalizedPoint, NormalizedPoint] = points.length >= 2
      ? [points[0], points[1]]
      : [{ x: bounds.x, y: bounds.y }, { x: bounds.x + bounds.width, y: bounds.y + bounds.height }];
    return {
      ...base,
      type,
      points: pair,
      strokeColor,
      strokeWidth,
      arrowEnds: type === 'arrow' ? normalizeArrowEnds(style.arrowEnds) : 'none',
      arrowStyle: style.arrowStyle === 'curved' ? 'curved' : 'straight'
    };
  }
  if (type === 'pencil' || type === 'highlighter' || type === 'polyline') {
    return {
      ...base,
      type: type === 'highlighter' ? 'marker' : type === 'polyline' ? 'pen' : 'pencil',
      points,
      closed: false,
      strokeColor,
      strokeWidth
    };
  }
  if (type === 'text') {
    return {
      ...base,
      type: 'text',
      text: typeof raw.text === 'string' ? raw.text : 'Text',
      color: strokeColor,
      backgroundColor: null,
      outlineColor: null,
      fontFamily: typeof style.fontFamily === 'string' ? style.fontFamily : 'Inter, system-ui, sans-serif',
      fontSize: positiveNumber(style.fontSize, 42),
      fontWeight: positiveNumber(style.fontWeight, 500),
      textAlign: 'left',
      curve: 0
    };
  }
  return null;
}

function normalizePoints(value: unknown): NormalizedPoint[] {
  if (!Array.isArray(value)) return [];
  return value
    .filter((point): point is Record<string, unknown> => Boolean(point && typeof point === 'object'))
    .map((point) => ({ x: numberValue(point.x, 0), y: numberValue(point.y, 0) }));
}

export function boundsFromPoints(points: NormalizedPoint[]): ElementBounds {
  if (!points.length) return { x: 0.2, y: 0.2, width: 0.2, height: 0.15 };
  const xs = points.map((point) => point.x);
  const ys = points.map((point) => point.y);
  const x = Math.min(...xs);
  const y = Math.min(...ys);
  return {
    x,
    y,
    width: Math.max(0.005, Math.max(...xs) - x),
    height: Math.max(0.005, Math.max(...ys) - y)
  };
}

function normalizeArrowEnds(value: unknown): LineElement['arrowEnds'] {
  return value === 'none' || value === 'start' || value === 'both' ? value : 'end';
}

function normalizeExportDefaults(value: unknown): ExportDefaults {
  const raw = value && typeof value === 'object' ? value as Record<string, unknown> : {};
  return {
    format: raw.format === 'png' || raw.format === 'webp' ? raw.format : 'jpeg',
    quality: clampNumber(raw.quality, 0.4, 1, 0.9),
    preserveMetadata: raw.preserveMetadata !== false
  };
}

function legacyName(type: string, index: number): string {
  const name = type === 'highlighter' ? 'Marker' : type === 'polyline' ? 'Pen path' : type || 'Object';
  return `${name.charAt(0).toUpperCase()}${name.slice(1)} ${index + 1}`;
}

function numberValue(value: unknown, fallback: number): number {
  return typeof value === 'number' && Number.isFinite(value) ? value : fallback;
}

function positiveNumber(value: unknown, fallback: number): number {
  const next = numberValue(value, fallback);
  return next > 0 ? next : fallback;
}

function clampNumber(value: unknown, min: number, max: number, fallback: number): number {
  return Math.max(min, Math.min(max, numberValue(value, fallback)));
}
